import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import {
  JobLogPatternDto,
  JobLogPatternService,
  PatternStatus,
  UpsertJobLogPatternRequest,
} from '../../../services/joblog-pattern.service';
import { Application } from '../../../interfaces/application.interface';
import { ApplicationService } from '../../../services/application.service';
import { LogMatcherContextService } from '../../../services/logmatcher-context.service';
import Swal from 'sweetalert2';
import { showErrorSwal } from '@asamco/utils';

type UiMode = 'list' | 'edit';

type UiJobLogPattern = JobLogPatternDto & {
  linkId?: number | null;
  description?: string | null;
  freshdeskArticleIds?: number[];

  freshdeskArticleRowIds?: number[];

  createdByUserId?: number | null;
  updatedByUserId?: number | null;
  createdByName?: string | null;
  updatedByName?: string | null;
};

// ✅ keep values aligned with backend
export enum JobLogType {
  Undefined = 0,
  Success = 1,
  Fail = 2,
  AmountMismatch = 3,
  Error = 4,
  Info = 6,
}

type PagerToken =
  | { kind: 'page'; value: number; isCurrent: boolean }
  | { kind: 'gap' };

@Component({
  selector: 'appshell-link-joblog-patterns-admin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './link-joblog-patterns-admin.component.html',
  styleUrl: './link-joblog-patterns-admin.component.scss',
})
export class LinkJoblogPatternsAdminComponent implements OnInit {
  mode: UiMode = 'list';
  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string>('');



  q = '';
  private readonly q$ = new BehaviorSubject<string>('');
  private readonly reload$ = new BehaviorSubject<void>(undefined);

  private latestPatterns: UiJobLogPattern[] = [];

  articleIdsText = '';

  // ✅ cleaner: null = no filter, remove legacy "5 = All"
  readonly jobLogTypeOptions = [
    { value: null as number | null, label: 'All (no filter)' },
    { value: JobLogType.Error, label: 'Error' },
    { value: JobLogType.Fail, label: 'Fail' },
    { value: JobLogType.Info, label: 'Info' },
    { value: JobLogType.Success, label: 'Success' },
    { value: JobLogType.AmountMismatch, label: 'Amount mismatch' },
    { value: JobLogType.Undefined, label: 'Undefined' },
  ] as const;

  readonly statusOptions = [
    { value: 1 as PatternStatus, label: 'Active' },
    { value: 0 as PatternStatus, label: 'Inactive' },
  ] as const;

  readonly applications$: Observable<Application[]> = this.applicationService.get().pipe(
    map(apps =>
      [...(apps ?? [])].sort((a, b) =>
        this.appLabel(a).localeCompare(this.appLabel(b), undefined, { sensitivity: 'base' })
      )
    ),
    catchError(() => of([])),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly patterns$: Observable<UiJobLogPattern[]> = combineLatest([
    this.q$.pipe(startWith('')),
    this.reload$.pipe(startWith(undefined)),
  ]).pipe(
    tap(() => {
      this.loading$.next(true);
      this.error$.next('');
    }),
    switchMap(([q]) =>
      this.patternService.list(q).pipe(
        map(items => (items ?? []) as UiJobLogPattern[]),
        catchError(() => {
          this.error$.next('Failed to load patterns.');
          return of([] as UiJobLogPattern[]);
        })
      )
    ),
    tap(items => {
      this.latestPatterns = items ?? [];
      this.loading$.next(false);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  matchText = '';
  onlyMatches = false;

  private readonly matchText$ = new BehaviorSubject<string>('');
  private readonly onlyMatches$ = new BehaviorSubject<boolean>(false);

  userLabel(name?: string | null, id?: number | null): string {
    if (name && name.trim()) return name.trim();
    if (typeof id === 'number') return `#${id}`;
    return '—';
  }

  readonly matchingIds$: Observable<Set<number>> = combineLatest([
    this.matchText$.pipe(startWith('')),
    this.reload$.pipe(startWith(undefined)),
  ]).pipe(
    switchMap(([text]) => {
      const msg = (text ?? '').trim();
      if (!msg) return of(new Set<number>());

      // Use current filter selectors if you want them to influence matching.
      // Here we use "All/no filter" by default.
      return this.patternService.matchPatterns({
        message: msg,
        activeOnly: true,
      }).pipe(
        map(res => new Set<number>((res?.patternIds ?? []).map(x => Number(x)))),
        catchError(() => of(new Set<number>()))
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Derive visible patterns from: server list(q) + match filtering
  readonly visiblePatterns$: Observable<UiJobLogPattern[]> = combineLatest([
    this.patterns$,
    this.matchingIds$,
    this.onlyMatches$.pipe(startWith(false)),
  ]).pipe(
    map(([patterns, matchIds, onlyMatches]) => {
      if (!onlyMatches) return patterns;
      return patterns.filter(p => matchIds.has(p.id));
    })
  );

  editingId: number | null = null;
  form: UpsertJobLogPatternRequest = this.emptyForm();

  constructor(
    private patternService: JobLogPatternService,
    private applicationService: ApplicationService,
    private router: Router,
    private route: ActivatedRoute,
    private ctx: LogMatcherContextService
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(qp => {
      const mode = qp.get('mode');
      const edit = qp.get('edit');

      if (mode === 'new') {
        this.openNewFromContext();
        return;
      }

      if (edit) {
        const id = Number(edit);
        if (!Number.isNaN(id)) {
          this.openEditByIdIfLoaded(id);
        }
      }

      combineLatest([
        this.q$.pipe(startWith('')),
        this.matchText$.pipe(startWith('')),
        this.onlyMatches$.pipe(startWith(false)),
        this.reload$.pipe(startWith(undefined)),
      ]).subscribe(() => this.setPage(1));
    });
  }

  formatUtc(v: string | Date | null | undefined): string {
    if (!v) return '—';

    let d: Date;

    if (v instanceof Date) {
      d = v;
    } else {
      // Force UTC interpretation
      const iso = v.endsWith('Z') ? v : v.replace(' ', 'T') + 'Z';
      d = new Date(iso);
    }

    if (Number.isNaN(d.getTime())) return '—';

    const pad = (n: number) => n.toString().padStart(2, '0');

    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();

    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

  page = 1;
  pageSize = 25;

  private readonly page$ = new BehaviorSubject<number>(1);
  private readonly pageSize$ = new BehaviorSubject<number>(25);

  readonly pageSizeOptions = [10, 25, 50, 100] as const;

  setPage(p: number) {
    const next = Math.max(1, Math.floor(p || 1));
    this.page = next;
    this.page$.next(next);
  }

  setPageSize(sz: number) {
    const next = Math.max(1, Math.floor(sz || 25));
    this.pageSize = next;
    this.pageSize$.next(next);
    this.setPage(1); // reset to first page
  }

  /** How many pages to show around the current page */
  private readonly pagerWindow = 2;

  private buildPager(current: number, total: number, window: number): PagerToken[] {
    if (total <= 1) return [{ kind: 'page', value: 1, isCurrent: true }];

    const clamp = (n: number) => Math.min(Math.max(1, n), total);
    const cur = clamp(current);

    const pages = new Set<number>();
    pages.add(1);
    pages.add(total);

    for (let i = cur - window; i <= cur + window; i++) {
      if (i >= 1 && i <= total) pages.add(i);
    }

    // When close to ends, pull in extra pages so the pager feels stable
    if (cur <= 1 + window) {
      for (let i = 1; i <= Math.min(total, 1 + window * 2 + 1); i++) pages.add(i);
    }
    if (cur >= total - window) {
      for (let i = Math.max(1, total - (window * 2 + 1)); i <= total; i++) pages.add(i);
    }

    const sorted = Array.from(pages).sort((a, b) => a - b);

    const tokens: PagerToken[] = [];
    for (let i = 0; i < sorted.length; i++) {
      const v = sorted[i];
      const prev = sorted[i - 1];

      if (i > 0 && prev !== undefined && v - prev > 1) {
        tokens.push({ kind: 'gap' });
      }

      tokens.push({ kind: 'page', value: v, isCurrent: v === cur });
    }

    return tokens;
  }

  readonly filteredCount$ = this.visiblePatterns$.pipe(
    map(items => items.length),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly totalPages$ = combineLatest([
    this.filteredCount$,
    this.pageSize$.pipe(startWith(this.pageSize)),
  ]).pipe(
    map(([count, size]) => Math.max(1, Math.ceil(count / Math.max(1, size)))),
    shareReplay({ bufferSize: 1, refCount: true })
  );


  readonly currentPage$ = combineLatest([
    this.page$.pipe(startWith(this.page)),
    this.totalPages$,
  ]).pipe(
    map(([page, total]) => Math.min(Math.max(1, page), total)),
    tap(p => {
      // keep "page" field in sync for template bindings
      if (this.page !== p) this.page = p;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly pagerTokens$ = combineLatest([
    this.currentPage$,
    this.totalPages$,
  ]).pipe(
    map(([cur, total]) => this.buildPager(cur, total, this.pagerWindow)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly pagedPatterns$ = combineLatest([
    this.visiblePatterns$,
    this.currentPage$,
    this.pageSize$.pipe(startWith(this.pageSize)),
  ]).pipe(
    map(([items, page, size]) => {
      const s = Math.max(1, size);
      const start = (page - 1) * s;
      return items.slice(start, start + s);
    })
  );



  onMatchTextChange(v: string) {
    this.matchText = v ?? '';
    this.matchText$.next(this.matchText);
  }

  toggleOnlyMatches(v: boolean) {
    this.onlyMatches = !!v;
    this.onlyMatches$.next(this.onlyMatches);
  }

  // ✅ used in template
  jobLogTypeLabel(v: number | null | undefined): string {
    // legacy safety: backend might still return 5
    if (v === 5 || v === null || v === undefined) return 'All';
    switch (v) {
      case JobLogType.Error:
        return 'Error';
      case JobLogType.Fail:
        return 'Fail';
      case JobLogType.Info:
        return 'Info';
      case JobLogType.Success:
        return 'Success';
      case JobLogType.AmountMismatch:
        return 'Amount mismatch';
      case JobLogType.Undefined:
        return 'Undefined';
      default:
        return `#${v}`;
    }
  }

  // Template helper
  linkIdOf(p: UiJobLogPattern): number | null {
    const v = p.linkId;
    return typeof v === 'number' ? v : null;
  }

  onFilterChange(v: string) {
    this.q = v ?? '';
    this.q$.next(this.q);
  }

  refresh() {
    this.reload$.next(undefined);
  }

  newPattern() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode: 'new', edit: null },
      queryParamsHandling: 'merge',
    });
  }

  private openNewFromContext() {
    const draft = this.ctx.draft$.value;
    const f = this.ctx.filters$.value;

    this.mode = 'edit';
    this.editingId = null;
    this.error$.next('');

    // ✅ normalize legacy "5" to null
    const normalizeJobLogType = (x: number | null | undefined) => (x === 5 ? null : x ?? null);

    this.form = {
      ...this.emptyForm(),
      title: draft.title ?? '',
      pattern: draft.pattern ?? '',
      category: draft.category ?? null,
      status: (draft.status ?? 1) as PatternStatus,

      linkId: draft.linkId ?? f.linkId ?? null,
      jobLogType: normalizeJobLogType(draft.jobLogType ?? f.jobLogType),
      fromApplicationId: draft.fromApplicationId ?? f.fromApplicationId ?? null,
      toApplicationId: draft.toApplicationId ?? f.toApplicationId ?? null,
      linkTypeId: draft.linkTypeId ?? null,

      description: (draft.description ?? '') as any,
      freshdeskArticleIds: (draft.freshdeskArticleIds ?? []) as any,
    };

    this.articleIdsText = (this.form.freshdeskArticleIds ?? []).join(', ');
  }

  private openEditByIdIfLoaded(id: number) {
    const found = this.latestPatterns.find(x => x.id === id);
    if (found) this.edit(found);
  }

  edit(p: UiJobLogPattern) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: p.id, mode: null },
      queryParamsHandling: 'merge',
    });

    this.mode = 'edit';
    this.editingId = p.id;
    this.error$.next('');

    const rowIds = ((p as any).freshdeskArticleRowIds ?? []) as number[];

    const normalizeJobLogType = (x: number | null | undefined) => (x === 5 ? null : x ?? null);

    this.form = {
      title: p.title ?? '',
      pattern: p.pattern ?? '',
      status: (p.status ?? 1) as PatternStatus,
      category: p.category ?? null,

      linkId: this.linkIdOf(p),
      jobLogType: normalizeJobLogType(p.jobLogType),

      fromApplicationId: p.fromApplicationId ?? null,
      toApplicationId: p.toApplicationId ?? null,
      linkTypeId: p.linkTypeId ?? null,

      description: (p as any).description ?? null,

      // keep sending external article IDs on save (your API expects FreshdeskArticleIds),
      // but for now just store row ids here so the field is filled:
      freshdeskArticleIds: rowIds as any,
    };

    // ✅ this is what fills the input
    this.articleIdsText = rowIds.join(', ');

  }

  cancel() {
    this.mode = 'list';
    this.editingId = null;
    this.form = this.emptyForm();
    this.articleIdsText = '';
    this.error$.next('');

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode: null, edit: null },
      queryParamsHandling: 'merge',
    });
  }

  parseArticleIds(input: string): number[] {
    return Array.from(
      new Set(
        (input ?? '')
          .split(/[\s,;]+/g)
          .map(s => s.trim())
          .filter(Boolean)
          .map(s => Number(s))
          .filter(n => Number.isFinite(n) && n > 0)
      )
    );
  }

  save() {
    this.error$.next('');

    const title = (this.form.title ?? '').trim();
    const pattern = (this.form.pattern ?? '').trim();

    if (!title) {
      Swal.fire({
        icon: 'error',
        title: 'Validation error',
        text: 'Title is required.',
      });
      return;
    }

    if (!pattern) {
      Swal.fire({
        icon: 'error',
        title: 'Validation error',
        text: 'Pattern is required.',
      });
      return;
    }

    const normalizedJobLogType =
      this.form.jobLogType === 5 ? null : this.form.jobLogType;

    const payload: UpsertJobLogPatternRequest = {
      ...this.form,
      title,
      pattern,
      category: (this.form.category ?? '').trim() || null,
      jobLogType: normalizedJobLogType,
      status: (this.form.status ?? 1) as PatternStatus,
      description: (this.form.description ?? '').trim() || null,
      freshdeskArticleIds: this.parseArticleIds(this.articleIdsText),
    };

    this.loading$.next(true);

    const req$ = this.editingId
      ? this.patternService.update(this.editingId, payload)
      : this.patternService.create(payload);

    req$
      .pipe(
        catchError((err) => {
          this.loading$.next(false);

          const message =
            err?.error?.message ||
            err?.error ||
            'An unexpected error occurred while saving.';

          showErrorSwal(err);

          return of(null);
        })
      )
      .subscribe(result => {
        this.loading$.next(false);

        if (!result) return;

        Swal.fire({
          icon: 'success',
          title: this.editingId ? 'Pattern updated' : 'Pattern created',
          timer: 1400,
          showConfirmButton: false,
        });

        this.cancel();
        this.refresh();
      });
  }

  toggleStatus(p: UiJobLogPattern) {
    const next: PatternStatus = p.status === 1 ? 0 : 1;

    this.loading$.next(true);
    this.patternService
      .setStatus(p.id, next)
      .pipe(
        catchError(() => {
          this.error$.next('Failed to update status.');
          return of(void 0);
        }),
        tap(() => this.loading$.next(false))
      )
      .subscribe(() => this.refresh());
  }

  test(p: UiJobLogPattern) {
    this.ctx.draft$.next({
      title: p.title ?? '',
      pattern: p.pattern ?? '',
      category: p.category ?? null,
      status: (p.status ?? 1) as PatternStatus,

      linkId: this.linkIdOf(p),
      jobLogType: (p.jobLogType === 5 ? null : p.jobLogType) ?? null,
      fromApplicationId: p.fromApplicationId ?? null,
      toApplicationId: p.toApplicationId ?? null,
      linkTypeId: p.linkTypeId ?? null,

      description: (p as any).description ?? null,
      freshdeskArticleIds: ((p as any).freshdeskArticleIds ?? []) as number[],
    });

    this.router.navigate(['../analysis'], {
      relativeTo: this.route,
      queryParams: { patternId: p.id },
    });
  }

  async confirmDelete(p: UiJobLogPattern) {
    const result = await Swal.fire({
      title: 'Delete pattern?',
      text: `Are you sure you want to delete "${p.title}"? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    this.deletePattern(p.id);
  }


  async confirmDeleteById(id: number) {
    const p = this.latestPatterns.find(x => x.id === id);
    const title = p?.title ?? `#${id}`;

    const result = await Swal.fire({
      title: 'Delete pattern?',
      text: `Are you sure you want to delete "${title}"? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    this.deletePattern(id);
  }

  private deletePattern(id: number) {
    this.error$.next('');
    this.loading$.next(true);

    this.patternService
      .delete(id)
      .pipe(
        catchError(() => {
          this.error$.next('Delete failed.');
          return of(void 0);
        }),
        tap(() => this.loading$.next(false))
      )
      .subscribe(() => {
        // If user deleted while editing the same pattern, exit edit mode
        if (this.editingId === id) {
          this.cancel();
        }
        this.refresh();
      });
  }

  private emptyForm(): UpsertJobLogPatternRequest {
    return {
      title: '',
      pattern: '',
      status: 1,
      category: null,

      linkId: null,
      jobLogType: null,

      fromApplicationId: null,
      toApplicationId: null,
      linkTypeId: null,

      description: null as any,
      freshdeskArticleIds: [] as any,
    };
  }

  appLabel(a: Application): string {
    return a.displayName || a.name || '';
  }

  appNameById(apps: Application[], id: number | null | undefined): string {
    if (!id) return '';
    const found = apps.find(x => x.id === id);
    return found ? this.appLabel(found) : `#${id}`;
  }

  trackById(_: number, item: UiJobLogPattern) {
    return item.id;
  }
}
